import { LoaderWithBackground } from '@carsys/ui'
import { dialogClasses } from '@mui/material/Dialog'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import SupportListWrapper from 'core/page/SupportListWrapper'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { v4 as uuidv4 } from 'uuid'
import { wholesalerTamplateList } from 'app-support/LKQAdmin/PendingEmailList/state/actions'
import { Grid } from 'semantic-ui-react'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'
import { downloadCsvFile, fetchAllWholesalerList, fetchFormData, fetchTemplateList } from 'app-support/LKQAdmin/Profile/state/actions'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import { RefreshDataFunc } from '@cbgms/components/grid/Knock/KnockDataLoader'
import { useBlobDownloader } from '@cbgms/base/hooks/use-blob-downloader'

const useStyles = makeStyles(() => ({
  button: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  formControl: {
    width: '220px'
  },
  inputLabel: {
    backgroundColor: 'white',
    padding: '0 0.5rem',
    transform: 'translate(14px, -6px) scale(0.75)',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  gridContainer: {
    width: '100%'
  },
  stackContainer: {
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.9rem'
  },
  dialogTitle: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  dialogContent: {
    maxHeight: '400px',
    overflowY: 'auto'
  },
  dialogFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '1rem'
  },
  boxRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #ddd',
    padding: '0.5rem 0',
    width: '100%'
  },
  boldText: {
    flex: 1.5,
    paddingRight: '1rem',
    fontWeight: 'bold'
  },
  valueText: {
    flex: 2,
    wordBreak: 'break-word'
  },
  menuItem: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: '8px',
    paddingRight: '8px',
    margin: 0
  }
}))

const CustomDataGrid: React.FC = () => {
  const excludedKeys = [
    'successMessage',
    'errorMessage',
    'dieselImage',
    'dieselPdf',
    'emailTemplateContent',
    'emailTemplateSubject',
    'particulateImage1',
    'particulateImage2'
  ]
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()
  const [isLoading, setIsLoading] = useState(false)
  const [seed, setSeed] = useState<string>(uuidv4())
  const [wholesalerList, setWholesalerList] = useState<{ label: string; value: string }[]>([])
  const [selectedWholesaler, setSelectedWholesaler] = useState<string | null>(null)
  const [templateList, setTemplateList] = useState<{ Name: string; UUID: string }[]>([])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([])
  const refreshData = useRef<RefreshDataFunc | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formData, setFormData] = useState<any>(null)
  const [formattedFormDataForTemplate, setFormattedFormDataForTemplate] = useState<any>({} as object)
  const download = useBlobDownloader()

  useEffect(() => {
    const fetchWholesalerList = async () => {
      try {
        setIsLoading(true)
        const response = await dispatch(fetchAllWholesalerList())
        const formattedOptions = response.Data.map((item: { Name: string; UUID: string }) => ({
          label: item.Name,
          value: item.UUID
        }))
        setWholesalerList(formattedOptions)
        setIsLoading(false)
      } catch (error) {
        notifier.error(t('dialog.errors.failedToFetchWholesalerList'))
        setIsLoading(false)
      }
    }
    fetchWholesalerList()
  }, [dispatch])

  const handleWholesalerChange = async (value: string) => {
    setSelectedWholesaler(value)
    setTemplateList([])
    setSelectedTemplates([])
    setSearchQuery('')
    setSeed(uuidv4())
    try {
      setIsLoading(true)
      const response = await dispatch(fetchTemplateList(value))
      setTemplateList(response.Data)
      setIsLoading(false)
    } catch (error) {
      notifier.error(t('dialog.errors.failedToFetchTemplateList'))
      setIsLoading(false)
    }
  }

  const handleTemplateSelectionChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[]
    setSelectedTemplates(value)
  }

  const handleSelectAll = () => {
    if (selectedTemplates.length === templateList.length) {
      setSelectedTemplates([])
    } else {
      setSelectedTemplates(templateList.map(template => template.UUID))
    }
  }

  const handleCheckboxChange = (templateUUID: string) => {
    if (selectedTemplates.includes(templateUUID)) {
      setSelectedTemplates(selectedTemplates.filter(id => id !== templateUUID))
    } else {
      setSelectedTemplates([...selectedTemplates, templateUUID])
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const filteredTemplates =
    searchQuery.length >= 3
      ? templateList.filter(template => template.Name.toLowerCase().includes(searchQuery.toLowerCase()))
      : templateList

  const onDoubleClickFunction = async (data: any) => {
    try {
      const formDataUUID = data?.FormData?.FormDataUUID
      if (!formDataUUID) {
        notifier.error(t('dialog.errors.invalidFormDataUUID'))
        return
      }

      setIsLoading(true)
      const responses = await dispatch(fetchFormData(formDataUUID))

      if (!responses || !responses.Data || typeof responses.Data !== 'object') {
        notifier.error(t('dialog.errors.invalidResponse'))
        setIsLoading(false)
        return
      }

      setFormData(responses.Data)
      setIsModalOpen(true)
      setIsLoading(false)
    } catch (error) {
      notifier.error(t('dialog.errors.failedToFetchFormData'))
      setIsLoading(false)
    }
  }

  const onPopupOpened = async (data: any) => {
    try {
      const formDataUUID = data?.FormData?.FormDataUUID
      if (!formDataUUID) {
        notifier.error(t('dialog.errors.invalidFormDataUUID'))
        return
      }

      setIsLoading(true)
      const responses = await dispatch(fetchFormData(formDataUUID))

      if (!responses || !responses.Data || typeof responses.Data !== 'object') {
        notifier.error(t('dialog.errors.invalidResponse'))
        setIsLoading(false)
        return
      }

      setFormData(responses.Data)
      setIsModalOpen(true)
      setIsLoading(false)
    } catch (error) {
      notifier.error(t('dialog.errors.failedToFetchFormData'))
      setIsLoading(false)
    }
  }

  const applyHandel = async () => {
    setSeed(uuidv4())
  }

  const handleResetFilters = () => {
    setSelectedWholesaler(null)
    setSelectedTemplates([])
    setTemplateList([])
    setSearchQuery('')
    setSeed(uuidv4())
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setFormData(null)
  }

  const renderFormData = (key: string, value: any, t: any, classes: any) => {
    if (value === null || value === undefined || typeof value === 'boolean' || excludedKeys.includes(key)) {
      return null
    }

    return (
      <Box key={key} className={classes.boxRow}>
        <Typography variant='body1' fontWeight='bold' className={classes.boldText}>
          {t(`dialog.${key}`, key)}:
        </Typography>
        <Typography variant='body1' className={classes.valueText}>
          {value}
        </Typography>
      </Box>
    )
  }

  const handleDownload = async (selectedTemplate: string) => {
    try {
      setIsLoading(true)
      if (!selectedWholesaler) {
        notifier.error(t('dialog.errors.invalidWholesaler'))
        return
      }
      const templateName = templateList.find(template => template.UUID === selectedTemplate)?.Name || selectedTemplate
      const formattedFileName = `${templateName.replace(/\s+/g, '-')}.csv`
      const response = await dispatch(downloadCsvFile(selectedWholesaler, selectedTemplate))

      if (response && response instanceof Blob) {
        download(response, formattedFileName)
        notifier.success(t('dialog.success.fileDownloaded'))
      } else {
        notifier.error(t('dialog.errors.invalidResponseForDownload'))
      }
    } catch (error) {
      notifier.error(t('dialog.errors.failedToFetchDownload'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (formData && typeof formData === 'object') {
      const formattedFormData = formData.reduce((acc: Record<string, any>, item: any) => {
        const key = Object.keys(item)[0]
        if (key && !excludedKeys.includes(key)) {
          acc[key] = item[key]
        }
        return acc
      }, {} as Record<string, any>)
      setFormattedFormDataForTemplate(formattedFormData)
    }
  }, [formData])
  return (
    <div data-testid='customers-table'>
      <LoaderWithBackground isLoading={isLoading} data-testid='api-loader' />
      <SupportListWrapper
        key={seed}
        scope='wholesaler-forms'
        type='list'
        hideExport={false}
        wholesalerFormModal={{ templateList }}
        onDownload={handleDownload}
        onPopupOpened={onPopupOpened}
        loadAction={params =>
          wholesalerTamplateList({ ...params, WholesalerUUID: selectedWholesaler ?? undefined, TemplateUUID: selectedTemplates.join(',') })
        }
        onDoubleClick={onDoubleClickFunction}
        translationKey='wholesaler-forms'
        tabItems={
          <Grid className={classes.gridContainer}>
            <Stack direction='row' mt={3} mb={3} ml={2} className={classes.stackContainer}>
              <FormControl variant='outlined' size='small' className={classes.formControl}>
                <InputLabel className={classes.inputLabel}>
                  {t('global.filter_by_wholsaler')}
                  <Typography component='span' color='error'>
                    *
                  </Typography>
                </InputLabel>
                <Select
                  value={selectedWholesaler}
                  onChange={e => handleWholesalerChange(e.target.value || '')}
                  label={t('global.filter_by_wholsaler')}
                  displayEmpty
                  renderValue={selected => {
                    if (!selected || selected.length === 0) {
                      return t('global.nofilters')
                    }
                    return wholesalerList.find(item => item.value === selected)?.label || t('global.nofilters')
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '300px'
                      }
                    }
                  }}
                >
                  {wholesalerList.map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                variant='outlined'
                size='small'
                sx={{
                  width: '200px'
                }}
              >
                <InputLabel
                  sx={{
                    backgroundColor: 'white',
                    px: 0.5,
                    transform: 'translate(14px, -6px) scale(0.75)',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {t('global.select_template')}
                  <Typography component='span' color='error'>
                    *
                  </Typography>
                </InputLabel>
                <Select
                  multiple
                  value={selectedTemplates}
                  onChange={handleTemplateSelectionChange}
                  label={t('global.select_template')}
                  disabled={templateList.length === 0}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }}
                  displayEmpty
                  renderValue={selected => {
                    if (selected.length === 0) {
                      return t('global.nofilters')
                    }
                    return selected.map(uuid => templateList.find(template => template.UUID === uuid)?.Name).join(', ')
                  }}
                >
                  <Box px={2} py={1}>
                    <TextField
                      size='small'
                      placeholder={t('global.search_templates')}
                      value={searchQuery}
                      onChange={handleSearchChange}
                      fullWidth
                    />
                  </Box>
                  <MenuItem className={classes.menuItem}>
                    <Checkbox
                      checked={selectedTemplates.length === templateList.length && templateList.length > 0}
                      indeterminate={selectedTemplates.length > 0 && selectedTemplates.length < templateList.length}
                      onChange={handleSelectAll}
                    />
                    <ListItemText primary={t('global.select_all')} />
                  </MenuItem>
                  {filteredTemplates.map(template => (
                    <MenuItem key={template.UUID} value={template.UUID} className={classes.menuItem}>
                      <Checkbox checked={selectedTemplates.includes(template.UUID)} onChange={() => handleCheckboxChange(template.UUID)} />
                      <ListItemText primary={template.Name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant='contained'
                color='primary'
                className={`ui tiny ml-half ${classes.button}`}
                data-testid='apply-filters-button'
                onClick={applyHandel}
                disabled={selectedTemplates.length === 0}
              >
                {t('global.apply_filters')}
              </Button>
              <Button
                variant='outlined'
                color='info'
                className={`ui tiny ml-half ${classes.button}`}
                data-testid='reset-button'
                onClick={handleResetFilters}
              >
                {t('global.reset')}
              </Button>
            </Stack>
          </Grid>
        }
        noDataPlaceholder={t('Customers.NoWholsalerFormsList')}
        onDataLoaderInitialized={newRefreshdata => (refreshData.current = newRefreshdata)}
      />
      <Dialog
        open={isModalOpen}
        onClose={handleModalClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        data-testid='confirmation-dialog'
        sx={{
          [`& .${dialogClasses.paper}`]: {
            width: '600px',
            height: '500px',
            maxWidth: 'none',
            position: 'relative'
          }
        }}
      >
        <DialogTitle id='alert-dialog-title' data-testid='dialog-title' className={classes.dialogTitle}>
          {t('dialog.title', 'Form Details')}
        </DialogTitle>
        <Box p={3} className={classes.dialogContent}>
          {formattedFormDataForTemplate && formData && typeof formData === 'object' ? (
            <div>{Object.entries(formattedFormDataForTemplate).map(([key, value]) => renderFormData(key, value, t, classes))}</div>
          ) : null}
        </Box>
        <Box className={classes.dialogFooter}>
          <Button variant='contained' color='primary' onClick={handleModalClose} sx={{ textTransform: 'none' }}>
            {t('global.close', 'Close')}
          </Button>
        </Box>
      </Dialog>
    </div>
  )
}

export default CustomDataGrid
